import * as React from 'react';
import { StyleSheet, StyleProp, ViewStyle, TextStyle } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { TabView, SceneMap, TabBar } from 'react-native-tab-view';
import { Card } from 'react-native-paper';
import { useNavigation } from '@react-navigation/native';
import { HeaderBackButton } from '@react-navigation/stack';
import i18n from 'i18n-js';

import initial, { useLayout, Layout } from '../constants/layout';
import { equal, EvolvingStatement, Statement } from '../constants/datagram';
import { Text, View } from './themed-view';
import Datagram from './datagram';
import {
  changing,
  from,
  hexagrams,
  index as hexagramIndex,
  mutated,
  title as hexagramTitle
} from '../constants/hexagram';
import Hexagram from './hexagram';
import { ReaderParamList } from '../types';
import useTranslation, { translated } from '../hooks/translation';

export interface Props {
  /** The line values array, admits undefined and changing 6 and 9 lines */
  lines: Statement;
}

type OptionsProps = { route: any; navigation: any };

export const options = ({ route, navigation }: OptionsProps) => {
  const params: ReaderParamList | undefined = route && route.params;
  let title;
  let headerLeft;

  if (params) {
    // @todo same logic as Exploring stack, when Reading
    const { reading } = params as ReaderParamList;
    if (reading && reading.lines) {
      title = hexagramTitle(reading.lines);
      headerLeft = () => <HeaderBackButton onPress={() => navigation.pop()} />;
    }
  }

  return {
    title,
    headerLeft
  };
};

const LeadingSymbol = '*'; // @todo, missing translation i18n.t('Asterisk');
const ConstituentSymbol = '†'; // @todo, missing translation i18n.t('Dagger');

const spreadText = (
  text: string,
  style: StyleProp<TextStyle>,
  key: string
): JSX.Element[] =>
  text.split('\n').map((p, k) => (
    // eslint-disable-next-line react/no-array-index-key
    <Text key={`${key}.${k}`} style={style}>
      {p}
    </Text>
  ));

const judgement = (
  translation: string,
  lines?: Statement
): JSX.Element | undefined => {
  const number = lines ? from(lines) : undefined;

  return number ? (
    <View key="judgement">
      {[
        spreadText(
          i18n.t(['translations', translation, 'authors']),
          [styles.text, styles.authors],
          'translation'
        ),
        translated(['rights', 'text'])
        ? spreadText(
            i18n.t(['translations', translation, 'rights', 'text']),
            [styles.text, styles.rights],
            'rights'
          )
        : spreadText(
            i18n.t(['translations', translation, 'rights']),
            [styles.text, styles.rights],
            'rights'
          ),
        translated(['rights', 'explanation'])
          ? spreadText(
              i18n.t(['translations', translation, 'rights', 'explanation']),
              [styles.text, styles.rights],
              'rights'
            )
          : undefined,
        translated('judgement', number)
          ? spreadText(
              i18n.t(`${number}.judgement`),
              [styles.text, styles.recap],
              'judgement'
            )
          : undefined,
        spreadText(
          i18n.t(`${number}.explanation`),
          [styles.text, styles.explanation],
          'explanation'
        )
      ]}
    </View>
  ) : undefined;
};

const image = (lines?: Statement) => {
  const index = lines ? hexagramIndex(lines) : undefined;
  return (
    <View style={styles.container} key="image">
      {(lines && <Datagram lines={lines} style={styles.datagram} />) ||
        undefined}
      {index !== undefined
        ? [
            <Text key="pictogram" style={styles.pictogram}>
              {hexagrams[index].pictogram}
            </Text>,
            // eslint-disable-next-line no-nested-ternary
            translated(['image', 'text'], index + 1) &&
            translated(['image', 'explanation'], index + 1) ? (
              <View key="text">
                {spreadText(
                  i18n.t(`${index + 1}.image.text`),
                  [styles.text, styles.recap],
                  'text'
                )}
                {spreadText(
                  i18n.t(`${index + 1}.image.explanation`),
                  [styles.text, styles.explanation],
                  'explanation'
                )}
              </View>
            ) : (
              spreadText(
                i18n.t(`${index + 1}.image`),
                [styles.text, styles.recap],
                'image'
              )
            )
          ]
        : undefined}
    </View>
  );
};

type LineProps = { number: number; line: number };

const Line = ({ number, line }: LineProps) => {
  const repcap: StyleProp<TextStyle>[] = [
    styles.text,
    styles.recap,
    styles.line
  ];
  const explanation: StyleProp<TextStyle>[] = [
    styles.text,
    styles.explanation,
    styles.line
  ];

  const index = number - 1;

  const leading =
    hexagrams[index].leading !== undefined &&
    hexagrams[index].leading?.indexOf(line) !== -1;
  const constituent =
    hexagrams[index].constituent !== undefined &&
    hexagrams[index].constituent?.indexOf(line) !== -1;

  if (leading || constituent) {
    const master = leading ? styles.leading : styles.constituent;
    repcap.push(master);
    explanation.push(master);
  }

  // return translated(['lines', 'detail', (line - 1).toString()], number) ?
  // eslint-disable-next-line no-nested-ternary
  const lineNumber = (line - 1).toString(10);

  return translated(['lines', 'detail', lineNumber, 'text'], number) &&
    translated(['lines', 'detail', lineNumber, 'explanation'], number) ? (
    <View key={line}>
      <Text key={`${line}.text`} style={repcap}>
        {line}. {leading && LeadingSymbol}
        {constituent && ConstituentSymbol}{' '}
        {i18n.t(`${number}.lines.detail.${line - 1}.text`)}
      </Text>
      <Text key={`${line}.explanation`} style={explanation}>
        {i18n.t(`${number}.lines.detail.${line - 1}.explanation`)}
      </Text>
    </View>
  ) : (
    <Text key={line} style={explanation}>
      {line}. {leading && LeadingSymbol}
      {constituent && ConstituentSymbol}{' '}
      {i18n.t(`${number}.lines.detail.${line - 1}`, { default: 'kkza' })}
    </Text>
  );
};

const detailedLines = (lines?: Statement) => {
  const number = lines ? from(lines) : undefined;
  const changed = changing(lines);

  // Copy of original array, interesting lines are only subject of detailedLines
  const interesting =
    // eslint-disable-next-line no-nested-ternary
    changed !== undefined
      ? changed
      : lines !== undefined
      ? [...lines]
      : undefined;
  // [ undefined, 6, undefined, 9, ... ]

  if ((number === 1 || number === 2) && !changed && interesting?.length === 6) {
    interesting?.push(number === 1 ? 8 : 7);
  }

  if (number) {
    const constituent = interesting?.findIndex((value, index) => {
      const c = hexagrams[number - 1].constituent;
      if (Number.isSafeInteger(value) && c) {
        return c.findIndex((reg) => reg === index + 1) !== -1;
      }
      return false;
    });

    const leading = interesting?.findIndex((value, index) => {
      const c = hexagrams[number - 1].leading;
      if (Number.isSafeInteger(value) && c) {
        return c.findIndex((reg) => reg === index + 1) !== -1;
      }
      return false;
    });

    return (
      <View key="lines">
        {translated(['lines', 'explanation'], number) && (
          <Text key="explanation" style={[styles.text, styles.explanation]}>
            {i18n.t(`${number}.lines.explanation`)}
          </Text>
        )}
        {interesting?.map((value, index) =>
          Number.isSafeInteger(interesting[index]) ? (
            // eslint-disable-next-line react/no-array-index-key
            <Line key={index} number={number} line={index + 1} />
          ) : undefined
        )}
        {leading !== -1 ? (
          <Text key="leading" style={[styles.text, styles.note]}>
            {LeadingSymbol} {i18n.t('Leading line')}
          </Text>
        ) : undefined}
        {constituent !== -1 ? (
          <Text key="constituent" style={[styles.text, styles.note]}>
            {ConstituentSymbol} {i18n.t('Constituent line')}
          </Text>
        ) : undefined}
      </View>
    );
  }

  return undefined;
};

const mutation = (navigation: any, lines?: Statement) => {
  const mutating = lines ? mutated(lines as EvolvingStatement) : undefined;
  const number = mutating ? from(mutating) : undefined;

  const onChoose = (i?: number) => {
    if (i !== undefined) {
      navigation.navigate('Exploring', {
        reading: { lines: hexagrams[i].lines }
      });
    }
  };

  return (
    <View>
      {number ? (
        <Hexagram number={number} onPress={() => onChoose(number - 1)} />
      ) : undefined}
    </View>
  );
};

const tab = (
  content?: JSX.Element,
  number?: number,
  style?: StyleProp<ViewStyle> | StyleProp<ViewStyle>[],
  key?: string
) => (
  <Card key={key || undefined} elevation={10} style={style}>
    <ScrollView>
      <Card.Content style={styles.content}>{content}</Card.Content>
    </ScrollView>
  </Card>
);

export default function Reading(props: Props): JSX.Element | null {
  const navigation = useNavigation();

  const rr = [
    { key: `judgment`, title: i18n.t('Judgment') },
    { key: `image`, title: i18n.t('Image') },
    { key: `lines`, title: i18n.t('Lines') }
  ] as Array<any>;

  const { lines } = props;
  const hexagram = from(lines);
  const mutating = mutated(lines);
  const immutable = equal(lines, mutating);

  const [index, setIndex] = React.useState(0);
  const [routes] = React.useState(rr);
  const [layout, setLayout] = React.useState<Layout>(initial);
  useLayout(layout, setLayout);

  // const { getTranslation, subscribeTranslation, isLoadingComplete } = useTranslation();

  // if (!isLoadingComplete()) {
  //   console.log(">>> Translation still loading");
  //   return null;
  // }

  // console.log(`>>> Translation loaded!! Using ${getTranslation()}`);

  // subscribeTranslation((value: string) => {
  //   // setTranslation(value)
  //   console.log(`>>> Translation updating to ${value} at reading, authors?`)
  // });

  const t = useTranslation() as string;

  const JudgmentRoute = () =>
    tab(
      judgement(t, lines),
      hexagram,
      [styles.scene, styles.judgement],
      'judgment'
    );

  const ImageRoute = () =>
    tab(image(lines), hexagram, [styles.scene, styles.image], 'image');

  const LinesRoute = () =>
    tab(detailedLines(lines), hexagram, [styles.scene], 'lines');

  const MutatedRoute = () =>
    tab(
      mutation(navigation, lines),
      hexagram,
      [styles.scene, styles.transformed],
      'mutation'
    );

  const scenes = {
    judgment: JudgmentRoute,
    image: ImageRoute,
    lines: LinesRoute
  };

  if (!immutable) {
    rr.push({ key: `transformed`, title: i18n.t('Mutation') });
  }

  const renderScene = SceneMap(
    immutable
      ? scenes
      : {
          ...scenes,
          transformed: MutatedRoute
        }
  );

  const renderTabBar = (pp: any) => (
    <TabBar
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...pp}
      style={styles.bar}
    />
  );

  return (
    <TabView
      navigationState={{ index, routes }}
      renderScene={renderScene}
      onIndexChange={setIndex}
      initialLayout={layout.window}
      renderTabBar={renderTabBar}
    />
  );
}

const styles = StyleSheet.create({
  bar: {
    fontSize: 4 // @todo
  },
  scene: {
    flex: 1
  },
  content: {
    backgroundColor: '#ff808026',
    paddingBottom: 0
  },
  container: {
    flexDirection: 'row-reverse'
  },
  pictogram: {
    fontSize: 48,
    height: 'auto'
  },
  judgement: {},
  text: {
    marginTop: 5,
    marginLeft: 5,
    marginBottom: 5
  },
  authors: {
    marginRight: 5,
    fontSize: 14,
    marginBottom: 4
  },
  rights: {
    marginRight: 5,
    fontSize: 10,
    marginTop: 0,
    marginBottom: 0,
  },
  rightsExplanation: {
    fontSize: 10
  },
  recap: {
    marginRight: 5,
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 7
  },
  line: {},
  explanation: {
    fontSize: 16
  },
  note: {
    marginTop: 2,
    fontSize: 14
  },
  leading: {
    backgroundColor: '#ff808026'
  },
  constituent: {
    backgroundColor: '#ffff8026'
  },
  image: {
    // marginLeft: 'auto'
  },
  datagram: {
    minWidth: 85,
    maxWidth: 85,
    justifyContent: 'flex-end'
  },
  transformed: {}
});
